/**
 * Icone dei giocatori fatte col generatore in app (Andrea 2026-10-01).
 *
 * L'elenco (giocatore, nome, versione) si carica una volta e vale per tutta
 * la sessione; `PlayerAvatar` lo consulta per nome prima di ricadere sulle
 * icone statiche di /public/avatars. L'immagine e' un URL pubblico versionato
 * (?v=): il browser la tiene in cache finche' non cambia.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface IconaInfo {
  giocatore_id: string;
  nome: string;
  versione: number;
}

export interface IconaDettaglio extends IconaInfo {
  config: Record<string, unknown> | null;
  png_data_url: string;
}

const CHIAVE = ["giocatori-icone"] as const;

export function urlIcona(info: IconaInfo): string {
  const base = (api.defaults.baseURL ?? "/api/v1").replace(/\/$/, "");
  return `${base}/giocatori/icone/${info.giocatore_id}.png?v=${info.versione}`;
}

const perNome = (rows: IconaInfo[]) => new Map(rows.map((r) => [r.nome, r]));

/** Mappa nome giocatore → icona salvata (vuota finche' non e' caricata). */
export function useIconeGiocatori() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: CHIAVE,
    queryFn: async () => (await api.get<IconaInfo[]>("/giocatori/icone")).data,
    enabled: isAuth,
    staleTime: 10 * 60 * 1000,
    select: perNome,
  });
}

export function useIconaGiocatore(giocatoreId: string | null) {
  return useQuery({
    queryKey: [...CHIAVE, giocatoreId],
    queryFn: async () => (await api.get<IconaDettaglio>(`/giocatori/${giocatoreId}/icona`)).data,
    enabled: !!giocatoreId,
    retry: false,
  });
}

export function useSalvaIconaGiocatore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      giocatoreId: string;
      pngDataUrl: string;
      config: Record<string, unknown> | null;
    }) =>
      (
        await api.put<IconaInfo>(`/giocatori/${args.giocatoreId}/icona`, {
          png_data_url: args.pngDataUrl,
          config: args.config,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CHIAVE }),
  });
}

export function useEliminaIconaGiocatore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (giocatoreId: string) => {
      await api.delete(`/giocatori/${giocatoreId}/icona`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHIAVE }),
  });
}
